import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '@/lib/adminApi';
import { Send, Save, Plus, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { slug: 'debloat', name: 'Debloat', color: '#22c55e' },
  { slug: 'asymmetry', name: 'Asymmetry', color: '#3b82f6' },
  { slug: 'coloring', name: 'Coloring', color: '#ec4899' },
  { slug: 'ratios', name: 'Ratios', color: '#f59e0b' },
  { slug: 'physique', name: 'Physique', color: '#ef4444' },
  { slug: 'styling', name: 'Styling', color: '#8b5cf6' },
  { slug: 'status', name: 'Status', color: '#06b6d4' },
];

export default function AdminReportsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialUser = params.get('user');
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [userData, setUserData] = useState(null);
  const [reportContent, setReportContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.reportsQueue();
      setQueue(r.data.queue);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    if (!selectedUser) return;
    adminAPI.getUser(selectedUser).then(r => {
      setUserData(r.data);
      const existing = r.data.report?.content || {};
      const initial = {};
      CATEGORIES.forEach(cat => {
        initial[cat.slug] = existing[cat.slug] || { recommendations: '', affiliate_links: [] };
      });
      setReportContent(initial);
    }).catch(() => toast.error('Failed to load user'));
  }, [selectedUser]);

  const updateRec = (slug, value) => {
    setReportContent(prev => ({ ...prev, [slug]: { ...prev[slug], recommendations: value } }));
  };

  const addAffiliate = (slug) => {
    setReportContent(prev => ({
      ...prev,
      [slug]: { ...prev[slug], affiliate_links: [...(prev[slug].affiliate_links || []), { name: '', url: '', note: '' }] }
    }));
  };

  const updateAffiliate = (slug, i, field, value) => {
    setReportContent(prev => {
      const links = [...(prev[slug].affiliate_links || [])];
      links[i] = { ...links[i], [field]: value };
      return { ...prev, [slug]: { ...prev[slug], affiliate_links: links } };
    });
  };

  const removeAffiliate = (slug, i) => {
    setReportContent(prev => ({
      ...prev,
      [slug]: { ...prev[slug], affiliate_links: prev[slug].affiliate_links.filter((_, idx) => idx !== i) }
    }));
  };

  const handleSave = async (status = 'review') => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await adminAPI.saveReport({ user_id: selectedUser, content: reportContent, status });
      toast.success('Report saved');
      loadQueue();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleSend = async () => {
    if (!window.confirm('Send this report by email to the user now?')) return;
    setSending(true);
    try {
      await handleSave('approved');
      const r = await adminAPI.sendReport({ user_id: selectedUser });
      if (r.data.success) toast.success('Report emailed!');
      else toast.error('Email failed');
      loadQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-reports-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>Reports</h1>
        <p className="text-purple-200/50 text-sm mt-1">Build, review, and send AI reports to users.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Queue Panel */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5">
              <h3 className="text-white font-semibold text-sm">Queue ({queue.length})</h3>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {loading && <div className="p-6 text-center text-purple-200/50 text-sm">Loading…</div>}
              {!loading && queue.length === 0 && <div className="p-6 text-center text-purple-200/50 text-sm">No users have started onboarding.</div>}
              {queue.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => setSelectedUser(u.user_id)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 transition ${selectedUser === u.user_id ? 'bg-purple-500/10 border-l-2 border-l-purple-400' : 'hover:bg-white/[0.03]'}`}
                  data-testid={`queue-${u.user_id}`}
                >
                  <p className="text-white text-sm font-medium">{u.name || u.email}</p>
                  <p className="text-purple-200/50 text-xs mt-0.5">{u.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] text-purple-200/60">{u.categories_filled}/7 filled</span>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${u.report_status === 'sent' ? 'bg-green-500/20 text-green-300' : u.report_status === 'approved' ? 'bg-blue-500/20 text-blue-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                      {u.report_status}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Editor Panel */}
        <div className="col-span-12 lg:col-span-8">
          {!selectedUser && (
            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-12 text-center">
              <FileText className="w-12 h-12 text-purple-300/30 mx-auto mb-3" />
              <p className="text-purple-200/50">Select a user from the queue to build their report.</p>
            </div>
          )}

          {selectedUser && userData && (
            <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">{userData.user.name}</p>
                  <p className="text-purple-200/50 text-xs">{userData.user.email}</p>
                </div>
                <button onClick={() => navigate(`/skyportal/users/${selectedUser}`)} className="text-xs text-purple-300 hover:text-purple-200">View profile →</button>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-5">
                {CATEGORIES.map(cat => {
                  const userCat = userData.categories.find(c => c.slug === cat.slug);
                  const hasData = userCat?.data;
                  return (
                    <div key={cat.slug} className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                          <h4 className="text-white font-medium">{cat.name}</h4>
                          {!hasData && <span className="text-[10px] text-yellow-300/70 uppercase">No user data</span>}
                        </div>
                      </div>

                      {hasData && (
                        <details className="mb-3">
                          <summary className="text-xs text-purple-300/70 cursor-pointer hover:text-purple-200">View user input</summary>
                          <pre className="text-[10px] text-purple-100/60 bg-black/30 mt-2 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(userCat.data, null, 2)}
                          </pre>
                        </details>
                      )}

                      <textarea
                        value={reportContent[cat.slug]?.recommendations || ''}
                        onChange={(e) => updateRec(cat.slug, e.target.value)}
                        rows={5}
                        placeholder={`AI recommendations for ${cat.name}… (edit before sending)`}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-purple-200/30 focus:outline-none focus:border-purple-400/50 resize-y"
                        data-testid={`rec-${cat.slug}`}
                      />

                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs uppercase tracking-wider text-purple-200/60 font-semibold">Affiliate Links</p>
                          <button onClick={() => addAffiliate(cat.slug)} className="text-xs text-purple-300 hover:text-purple-200 flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add
                          </button>
                        </div>
                        {(reportContent[cat.slug]?.affiliate_links || []).map((link, i) => (
                          <div key={i} className="flex gap-2 mb-2">
                            <input value={link.name} onChange={e => updateAffiliate(cat.slug, i, 'name', e.target.value)} placeholder="Product name" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                            <input value={link.url} onChange={e => updateAffiliate(cat.slug, i, 'url', e.target.value)} placeholder="https://amzn.to/…" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                            <input value={link.note} onChange={e => updateAffiliate(cat.slug, i, 'note', e.target.value)} placeholder="Note" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                            <button onClick={() => removeAffiliate(cat.slug, i)} className="text-red-300/60 hover:text-red-300"><X className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-6 py-4 border-t border-white/5 flex gap-3">
                <button onClick={() => handleSave('review')} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition flex items-center justify-center gap-2" data-testid="save-draft-btn">
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Draft'}
                </button>
                <button onClick={handleSend} disabled={sending} className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2" style={{ background: 'linear-gradient(145deg, #ec4899, #be185d)' }} data-testid="send-report-btn">
                  <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Approve &amp; Send Email'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
